/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { usePointerGesture } from './usePointerGesture';
/**
 * Draggable region
 */
type DraggableRegion = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

type DragDetail = {
  x: number;
  y: number;
  dx: number;
  dy: number;
  regionOffsetX: number;
  regionOffsetY: number;
  originalEvent: PointerEvent;
};

type UseDragOptions = {
  captureTargetRef: RefObject<HTMLDivElement>;
  draggableRegion?: DraggableRegion;
  onDragStart?: (detail: Omit<DragDetail, 'dx' | 'dy'>) => void;
  onDragMove?: (detail: DragDetail) => void;
  onDragEnd?: (detail: DragDetail) => void;
  isDisabled?: boolean;
};
/**
 * Whether point with coordinates are in the draggable region
 */
const inDraggableRegion = (x: number, y: number, region?: DraggableRegion) => {
  if (!region) return true;
  const { xMin, xMax, yMin, yMax } = region;
  return x >= xMin && x <= xMax && y >= yMin && y <= yMax;
};

/**
 * Returns region offset
 */
const getRegionOffset = (x: number, y: number, region?: DraggableRegion) => {
  if (!region) return { regionOffsetX: x, regionOffsetY: y };
  return { regionOffsetX: x - region.xMin, regionOffsetY: y - region.yMin };
};

const useDrag = ({
  captureTargetRef,
  draggableRegion,
  onDragStart,
  onDragMove,
  onDragEnd,
  isDisabled = false
}: UseDragOptions) => {
  const inactiveState = { isDragging: false, x0: -1, y0: -1, regionOffsetX: -1, regionOffsetY: -1 };
  const dragState = useRef(inactiveState);

  const handlePointerEvent = (e: PointerEvent, cb?: (detail: DragDetail) => void) => {
    const { isDragging, x0, y0, regionOffsetX, regionOffsetY } = dragState.current;
    if (!isDragging) return;
    const x = e.pageX;
    const y = e.pageY;
    cb && cb({ x, y, dx: x - x0, dy: y - y0, regionOffsetX, regionOffsetY, originalEvent: e });
  };

  const dragProps = usePointerGesture({
    captureTargetRef,
    numPointers: 1, // Only allow 1 pointer dragging
    onExtraneousPointerDown: () => {
      dragState.current = inactiveState;
    },
    onPointerGestureDown: ({ pointers: [e] }) => {
      const x = e.pageX;
      const y = e.pageY;
      if (e.button !== 0 || !inDraggableRegion(x, y, draggableRegion)) return;
      const { regionOffsetX, regionOffsetY } = getRegionOffset(x, y, draggableRegion);
      dragState.current = { isDragging: true, x0: x, y0: y, regionOffsetX, regionOffsetY };
      onDragStart && onDragStart({ x, y, regionOffsetX, regionOffsetY, originalEvent: e });
    },
    onPointerGestureMove: ({ pointers: [e] }) => {
      handlePointerEvent(e, onDragMove);
    },
    onPointerGestureEnd: ({ pointers: [e] }) => {
      handlePointerEvent(e, onDragEnd);
      dragState.current = inactiveState;
    }
  });

  return isDisabled ? {} : dragProps;
};

export { useDrag };
